/**
 * Утилиты для форматирования данных
 * 
 * Функции для форматирования цен, дат, чисел и других данных
 */

import type { ProductImage } from '@/entities/product/model/types'

// ============================================================
// Константы для блина (прессованный чай)
// ============================================================

/** Вес блина (пуэр, шен, шу и т.д.) в граммах */
export const CAKE_WEIGHT = 357



/**
 * Рассчитывает цену за блин
 * Если cakePrice указана вручную - использует её
 * Иначе рассчитывает по формуле: k = 0.89 - 0.0032 * pricePerGram
 * 
 * @param pricePerGram - цена за грамм
 * @param cakePrice - цена за блин из админки (опционально)
 * @returns цена за блин
 */
export function calculateCakePrice(pricePerGram: number, cakePrice?: number | null, cakeWeight?: number | null): number {
  // Если цена за блин указана вручную - используем её
  if (cakePrice && cakePrice > 0) {
    return cakePrice
  }

  const effectiveCakeWeight = cakeWeight ?? CAKE_WEIGHT

  // Для нестандартного веса блина формула не применяется — только ppg * вес
  if (effectiveCakeWeight !== CAKE_WEIGHT) {
    return pricePerGram * effectiveCakeWeight
  }

  // Рассчитываем по формуле с коэффициентом (стандартный вес 357г)
  const k = 0.89 - 0.0032 * pricePerGram
  const price = pricePerGram * CAKE_WEIGHT * k

  // Округляем до 100 рублей
  return Math.round(price / 100) * 100
}

/** Стандартные варианты веса для чая */
export const STANDARD_WEIGHTS = [10, 50, 100] as const

/** Все варианты веса включая блин */
export const ALL_WEIGHTS = [...STANDARD_WEIGHTS, CAKE_WEIGHT] as const

/**
 * Получает варианты веса с учётом доступности блина
 * @param isCakeAvailable - доступна ли продажа блином
 * @param isCakeOnly - продаётся только блином (без 10/50/100г)
 * @param cakeWeight - пользовательский вес блина (null = 357г)
 * @param currentWeight - текущий выбранный вес (если блин, всегда включаем)
 * @returns массив доступных вариантов веса
 */
export function getWeightOptions(
  isCakeAvailable: boolean,
  isCakeOnly?: boolean,
  cakeWeight?: number | null,
  currentWeight?: number,
): number[] {
  const effectiveCakeWeight = cakeWeight ?? CAKE_WEIGHT
  if (isCakeOnly) return [effectiveCakeWeight]
  const showCake = isCakeAvailable || currentWeight === effectiveCakeWeight
  return showCake ? [...STANDARD_WEIGHTS, effectiveCakeWeight] : [...STANDARD_WEIGHTS]
}

// ============================================================
// Хелперы для изображений
// ============================================================

/**
 * Получает URL изображения из ProductImage объекта или строки
 * 
 * @param image - ProductImage объект или строка URL
 * @param fallback - fallback URL если изображение не найдено
 * @returns URL изображения
 */
export function getImageUrl(
  image: ProductImage | string | undefined | null,
  fallback = '/placeholder-product.svg'
): string {
  if (!image) return fallback
  if (typeof image === 'string') return image
  return image.url || fallback
}

/**
 * Получает первый URL из массива ProductImage
 * 
 * @param images - массив ProductImage объектов
 * @param fallback - fallback URL если изображения нет
 * @returns URL первого изображения
 */
export function getFirstImageUrl(
  images: ProductImage[] | undefined | null,
  fallback = '/placeholder-product.svg'
): string {
  if (!images || images.length === 0) return fallback
  return images[0]?.url || fallback
}

/**
 * Форматирует цену в рубли
 * 
 * Использует Intl.NumberFormat для форматирования цены в российский формат.
 * 
 * @param price - цена в рублях (число)
 * @returns отформатированная строка с валютой (например, "1 000 ₽")
 * 
 * @example
 * ```ts
 * formatPrice(1000) // "1 000 ₽"
 * formatPrice(100) // "100 ₽"
 * ```
 */
export function formatPrice(price: number): string {
  // Защита от NaN и undefined
  const safePrice = isNaN(price) || price === undefined || price === null ? 0 : price
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(safePrice)
}

/**
 * Форматирует дату в читаемый формат
 * 
 * Форматирует дату в формат "15 января 2024" (русская локаль).
 * 
 * @param date - дата в виде Date объекта или строки
 * @returns отформатированная строка даты
 * 
 * @example
 * ```ts
 * formatDate(new Date('2024-01-15')) // "15 января 2024"
 * formatDate('2024-01-15') // "15 января 2024"
 * ```
 */
export function formatDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date
  return new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(d)
}

/**
 * Форматирует дату и время в читаемый формат
 * 
 * Форматирует дату и время в формат "15 января 2024, 14:30" (русская локаль).
 * 
 * @param date - дата в виде Date объекта или строки
 * @returns отформатированная строка даты и времени
 * 
 * @example
 * ```ts
 * formatDateTime(new Date('2024-01-15T14:30:00')) // "15 января 2024, 14:30"
 * ```
 */
export function formatDateTime(date: Date | string | null | undefined): string {
  if (!date) return 'Дата уточняется'
  const d = typeof date === 'string' ? new Date(date) : date
  if (isNaN(d.getTime())) return 'Дата уточняется'
  return new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d)
}

/**
 * Форматирует вес товара
 * 
 * Добавляет единицу измерения "г" к весу.
 * Для блина (357г) отображает "Блин (357г)"
 * 
 * @param weight - вес в граммах
 * @returns строка с весом и единицей измерения (например, "100г" или "Блин (357г)")
 * 
 * @example
 * ```ts
 * formatWeight(100) // "100г"
 * formatWeight(357) // "Блин (357г)"
 * ```
 */
export function formatWeight(weight: number, cakeWeight?: number | null): string {
  const effectiveCakeWeight = cakeWeight ?? CAKE_WEIGHT
  if (weight === effectiveCakeWeight) {
    return `Блин (${effectiveCakeWeight}г)`
  }
  return `${weight}г`
}

/**
 * Форматирует вес для кнопки/чипа (короткая версия)
 *
 * @param weight - вес в граммах
 * @param cakeWeight - пользовательский вес блина (null = 357г)
 * @returns короткая строка ("10г", "50г", "100г", "Блин")
 */
export function formatWeightShort(weight: number, cakeWeight?: number | null): string {
  const effectiveCakeWeight = cakeWeight ?? CAKE_WEIGHT
  if (weight === effectiveCakeWeight) {
    return 'Блин'
  }
  return `${weight}г`
}

/**
 * Форматирует время в формат ЧЧ:ММ
 * 
 * @param date - дата в виде Date объекта или строки
 * @returns отформатированная строка времени (например, "14:30")
 * 
 * @example
 * ```ts
 * formatTime(new Date('2024-01-15T14:30:00')) // "14:30"
 * ```
 */
export function formatTime(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date
  return new Intl.DateTimeFormat('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(d)
}

/**
 * Форматирует дату в относительный формат
 * 
 * Возвращает относительное описание даты: "Сегодня", "Вчера", "2 дня назад" и т.д.
 * Если дата старше недели, возвращает обычное форматирование даты.
 * 
 * @param date - дата в виде Date объекта или строки
 * @returns относительное описание даты
 * 
 * @example
 * ```ts
 * formatRelativeDate(new Date()) // "Сегодня"
 * formatRelativeDate(new Date(Date.now() - 86400000)) // "Вчера"
 * formatRelativeDate(new Date('2024-01-15')) // "15 января 2024"
 * ```
 */
export function formatRelativeDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date
  const now = new Date()
  const diffMs = now.getTime() - d.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  
  if (diffDays === 0) {
    return 'Сегодня'
  }
  if (diffDays === 1) {
    return 'Вчера'
  }
  if (diffDays < 7) {
    return `${diffDays} дня назад`
  }
  
  // Если больше недели, возвращаем обычную дату
  return formatDate(d)
}

